import type { UseQueryResult } from '@tanstack/react-query';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useContext } from 'react';
import type Maybe from 'true-myth/maybe';

import { ApplicationContext } from '~/shared/context/context';
import type { GeneralFilter } from '~/shared/data-contract/filter';

import type { GlobalSearchLearningOpportunityResponse } from '../../../data-contract/global-search';
import { globalSearchLearningOpportunityResponseSchema } from '../../../data-contract/global-search';
import type { SortOption } from '../../pages/talent-directory/sort-option';

export const useGlobalSearchDirectoryLearningOpportunities = ({
    filterPayload,
    page,
    pageSize,
    searchTerm,
    sortOptions,
}: {
    readonly filterPayload: Maybe<GeneralFilter>;
    readonly page: number;
    readonly pageSize: number;
    readonly searchTerm: Maybe<string>;
    readonly sortOptions: readonly SortOption[];
}): UseQueryResult<GlobalSearchLearningOpportunityResponse, Error> => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const hasSearchTerm = searchTerm.isJust;
    const pathname = hasSearchTerm
        ? '/global/search/learning-opportunities'
        : '/global/directory/learning-opportunities/filter';
    const operation = searchTerm.isJust ? 'get' : 'postJson';

    return useQuery({
        placeholderData: keepPreviousData,
        queryFn: async () => {
            const result = searchTerm.isJust
                ? await validatingCobrainerApiFetcher.get(globalSearchLearningOpportunityResponseSchema, {
                      pathname,
                      requestOptions: {
                          searchParams: {
                              page: String(page),
                              searchTerm: searchTerm.value,
                              size: String(pageSize),
                              sort: sortOptions
                                  .map(({ direction, field }) => (direction === 'desc' ? `-${field}` : field))
                                  .join(','),
                          },
                      },
                  })
                : await validatingCobrainerApiFetcher.postJson(globalSearchLearningOpportunityResponseSchema, {
                      pathname,
                      payload: {
                          filter: filterPayload.unwrapOr(undefined),
                          paged: {
                              page: String(page),
                              size: String(pageSize),
                          },
                          sorted: {
                              fields: sortOptions,
                          },
                      },
                  });

            if (result.isOk) {
                return result.value;
            }
            throw result.error;
        },
        queryKey: [pathname, operation, filterPayload, page, pageSize, sortOptions, searchTerm],
    });
};
